/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { Button, Dialog, Grid, InlineNotification, Typography2 } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfilePhotoUploader from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ProfilePhotoUploader';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  formContainer: css`
    input {
      margin-bottom: 0;
    }
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const EditProfilePhotoDialog = ({ isOpen, onClose }: Props) => {
  const { profileState, updateLearnerProfile } = useContext(ProfileContext);
  const { HeadingGroup, Content, Actions } = Dialog;
  const [profilePhoto, setProfilePhoto] = useState(profileState?.profilePhoto);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClose = () => {
    setErrorMessage('');
    setProfilePhoto(profileState?.profilePhoto);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    const updatedFields = {
      profilePhoto,
    };

    try {
      await updateLearnerProfile(updatedFields);
      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);
      setErrorMessage(_t('There was an error saving the form, please try again'));
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} width="medium" variant="standard" aria-describedby="dialog-content">
      <HeadingGroup>{_t('Profile Photo')}</HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_intro_profile_photo_dialog"
          requireFullyVisible={false}
        >
          <Grid css={styles.formContainer} container spacing={16}>
            <Grid item xs={12}>
              <Typography2 component="p" variant="bodyPrimary">
                {_t(
                  'Choose a photo that shows your face clearly and has a professional look. If you use other professional platforms, it’s a good idea to keep them consistent.'
                )}
              </Typography2>
            </Grid>
            <Grid item xs={12}>
              <ProfilePhotoUploader value={profilePhoto} onChange={setProfilePhoto} />
            </Grid>
          </Grid>
          {errorMessage && <InlineNotification severity="error">{errorMessage}</InlineNotification>}
        </TrackedDiv>
      </Content>
      <Actions>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving}
          loading={isSaving}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_save_intro_profile_photo"
          requireFullyVisible={false}
          withVisibilityTracking={true}
        >
          {isSaving ? _t('Saving...') : _t('Save')}
        </Button>
      </Actions>
    </Dialog>
  );
};

const ErrorBoundEditProfilePhotoDialog = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="EditProfilePhotoDialog"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the edit profile photo dialog. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <EditProfilePhotoDialog {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundEditProfilePhotoDialog;
